<script lang="typescript">
    import { state, setState } from "./stores.js";
    import { MessageType, FocalType } from "./types";
    import {
        computeTotalPrice,
        displayLensCodes,
        getAvailableBaseLensKeys,
        getBaseLensName,
        getBaseLensObject,
        getBasePrice,
        getLensDetails,
        getLogo,
        getReadingAddString,
        getRxString,
    } from "./utils";
    import { fly } from "svelte/transition";
    import { onDestroy } from "svelte";
    import { lensTierNames, upgradeLensTierNames, lensOption } from "./appData.js";
    import { push } from "svelte-spa-router";
    import { upgradeLensImages } from "./appData.js";
    import LensCard from "./LensCard.svelte";
    import LensBenefitCard from "./LensBenefitCard.svelte";
    import FeaturedLensOptionsCard from "./FeaturedLensOptionsCard.svelte";

    if ($state.rxId === null) window.location.replace("/");

    const lensObject = getBaseLensObject($state.focalType);
    const upgradeLensKeys = lensObject[$state.upgradeLensKey].upgradeLensCodes;
    // combine the base lens keys and the upgrade lens keys
    const allLensKeys = [$state.upgradeLensKey, ...upgradeLensKeys];
    const selectBaseLens = (lensTier: number): void => {
        // set the lens tier and base lens key, then go to the options page
        setState({
            message: MessageType.SET_BASE_LENS,
            lensTier,
            baseLensKey: allLensKeys[lensTier],
        });
        push("/upgrades/options/");
    };

    const baseLensKeys = getAvailableBaseLensKeys($state.focalType, $state.readingAdd, $state.rxId);
    const previousLensTier = baseLensKeys.indexOf($state.upgradeLensKey);
    const lensCardHeightClass =
        $state.focalType === FocalType.VARIFOCAL ? "h-60 upgrade" : "h-48 upgrade";

    var optionChecked = Array(baseLensKeys.length)
        .fill()
        .map(() => new Array());

    var prices = Array(baseLensKeys.length);
    Object.entries(getLensDetails(lensObject, allLensKeys)).forEach(([key, value]) => {
        prices[key] = getBasePrice(value, $state.region);
    });

    const updatePrice = (lensTier: number, lensOption) => {
        // only allow one option per lens
        Object.entries(optionChecked[lensTier]).forEach(([key, value]) => {
            if (key !== lensOption) {
                optionChecked[lensTier][key] = false;
            }
        });
        // update the displayed price to include the selection option
        var variant = null;
        if (optionChecked[lensTier][lensOption]) {
            variant = lensOption;
        }
        prices[lensTier] = computeTotalPrice(
            lensObject,
            allLensKeys[lensTier],
            variant,
            null,
            $state.region
        );
    };

    onDestroy(() => window.scrollTo(0, 0));
</script>

<section>
    {#if $state.focalType === FocalType.VARIFOCAL}
        <div class="w-full text-white shadow-inner bg-primary">
            <h3 class="py-2 mx-auto font-normal w-max">
                <span class="font-medium">Distance:</span>
                {getRxString($state.focalType, $state.rxId)},
                <span class="font-medium">Reading Add:</span>
                {getReadingAddString($state.readingAdd)}
            </h3>
        </div>
    {/if}

    <div class="page-container grid grid-cols-3 invisible sm:visible">
        <div class="col-span-1 text-center text-white bg-gray-700 mb-2 rounded-md py-2">
            {lensTierNames[previousLensTier]}
        </div>
        <div class="col-start-2 col-span-2 text-center text-white bg-gray-500 mb-2 rounded-md py-2">
            Upgraded lens design options
        </div>
    </div>

    <div class="page-container local-container">
        {#each getLensDetails(lensObject, allLensKeys) as option, i}
            <div
                id="lens-column"
                class="flex flex-col items-center w-full pt-12 md:w-1/3 sm:min-h-screen"
                in:fly={{ y: 100, duration: 500, delay: i * 200 }}
            >
                <h1 class="text-4xl font-normal">
                    {upgradeLensTierNames[i]}
                </h1>
                <LensCard
                    bind:price={prices[i]}
                    name={getBaseLensName(option, $state.region)}
                    image={upgradeLensImages[i].src}
                    baseHeightClass={lensCardHeightClass}
                />
                {#if displayLensCodes(option)}
                    <p>{displayLensCodes(option)}</p>
                {/if}
                <div class="w-full px-6 sm:h-20 md:h-18 lg:h-16">
                    {#if i == 0}
                        Entry-level single focus lens design, which depending on the prescription,
                        can limit the width of fully clear vision
                    {/if}
                    {#if i == 1}
                        Lens that provides a wide field of view, improves image clarity, acuity and
                        sharp vision across wide areas of the lens
                    {/if}
                    {#if i == 2}
                        All the features of the Optimised Lens, with additional personalisation,
                        taking the wearers frame fitting into account
                    {/if}
                </div>

                {#if typeof option.name === "string"}
                    <div class="w-full px-6 sm:h-52 md:h-40 lg:h-24">
                        {option.description}
                    </div>
                {/if}

                <!-- option benefits -->
                <div class="flex flex-col items-stretch w-full py-6">
                    {#each option.benefits as benefit}
                        <LensBenefitCard {...benefit} />
                    {/each}
                </div>
                <!-- featured options -->
                {#each option.variants as variant, v}
                    {#if variant.optionType === lensOption.TRANSITIONS || variant.optionType === lensOption.TRANSITIONS_XTRA}
                        <FeaturedLensOptionsCard
                            {variant}
                            logo={getLogo(variant.optionType)}
                            lensTier={i}
                            baseLensKey={allLensKeys[i]}
                            primaryVariantIndex={v}
                            bind:checked={optionChecked[i][variant.optionType]}
                            onToggle={() => updatePrice(i, variant.optionType)}
                        />
                    {/if}
                {/each}
                <button
                    type="button"
                    class="w-2/3 px-4 py-2 mt-auto mb-12 text-lg btn-primary"
                    on:click={() => selectBaseLens(i)}
                >
                    Discover additional lens options
                </button>
            </div>
        {/each}
    </div>
</section>

<style>
    #lens-column:nth-child(2) {
        @apply bg-primary-fade;
    }

    .local-container {
        @apply flex-row justify-center flex-wrap sm:flex-nowrap;
        @apply items-stretch overflow-y-hidden;
    }
</style>
